/**
 * VueLayers
 * Web map Vue components with the power of OpenLayers
 *
 * @package vuelayers
 * @author Vladimir Vershinin <ghettovoice@gmail.com>
 * @version 0.11.37
 * @license MIT
 * @copyright (c) 2017-2021, Vladimir Vershinin <ghettovoice@gmail.com>
 */
import _asyncToGenerator from '@babel/runtime-corejs3/helpers/esm/asyncToGenerator';
import _mapInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/map';
import _regeneratorRuntime from '@babel/runtime-corejs3/regenerator';
import { distinctUntilChanged } from 'rxjs/_esm5/internal/operators/distinctUntilChanged';
import { map } from 'rxjs/_esm5/internal/operators/map';
import { throttleTime } from 'rxjs/_esm5/internal/operators/throttleTime';
import { boundingExtent, findPointOnSurface, getGeometryId, initializeGeometry, setGeometryId, transforms } from '../ol-ext';
import { observableFromOlEvent } from '../rx-ext';
import { hasGeometry } from '../util/assert';
import { isEqual } from '../util/minilo';
import mergeDescriptors from '../util/multi-merge-descriptors';
import cmp from './ol-virt-cmp';
import projTransforms from './proj-transforms';
import useMapCmp from './use-map-cmp';

var geometry = {
  mixins: [cmp, useMapCmp, projTransforms],
  props: {
    /**
     * Coordinates in the map view projection.
     * @type {number[]}
     */
    coordinates: {
      type: Array,
      required: true,
      validator: function validator(val) {
        return val.length;
      }
    }
  },
  computed: {
    /**
     * @type {string}
     * @abstract
     * @readonly
     */
    type: function type() {
      throw new Error('Not implemented computed property');
    },

    /**
     * @type {number[]|undefined}
     */
    extent: function extent() {
      if (this.extentViewProj && this.resolvedDataProjection) {
        return this.extentToDataProj(this.extentViewProj);
      }
    },

    /**
     * @type {number[]|undefined}
     */
    extentViewProj: function extentViewProj() {
      if (this.rev && this.$geometry) {
        return this.$geometry.getExtent();
      }
    },

    /**
     * @type {number[]|undefined}
     */
    point: function point() {
      if (this.pointViewProj && this.resolvedDataProjection) {
        return this.pointToDataProj(this.pointViewProj);
      }
    },

    /**
     * @type {number[]}
     */
    pointViewProj: function pointViewProj() {
      if (this.rev && this.$geometry) {
        return findPointOnSurface(this.$geometry);
      }
    },

    /**
     * @type {number[]|undefined}
     */
    coordinatesViewProj: function coordinatesViewProj() {
      if (this.rev && this.$geometry) {
        return this.$geometry.getCoordinates();
      }
    }
  },
  methods: {
    /**
     * @return {module:ol/geom/Geometry~Geometry|Promise<module:ol/geom/Geometry~Geometry>}
     * @protected
     */
    createOlObject: function createOlObject() {
      var _this = this;

      return _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var geometry;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                _context.next = 2;
                return _this.createGeometry();

              case 2:
                geometry = _context.sent;
                initializeGeometry(geometry, _this.id);
                return _context.abrupt("return", geometry);

              case 5:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }))();
    },

    /**
     * @return {module:ol/geom/Geometry~Geometry|Promise<module:ol/geom/Geometry~Geometry>}
     * @protected
     * @abstract
     */
    createGeometry: function createGeometry() {
      throw new Error('Not implemented method');
    },

    /**
     * @return {number[]}
     */
    getCoordinates: function getCoordinates() {
      hasGeometry(this);
      return this.toDataProj(this.$geometry.getCoordinates());
    },

    /**
     * @param {number[]} coordinates
     */
    setCoordinates: function setCoordinates(coordinates) {
      hasGeometry(this);
      this.$geometry.setCoordinates(this.toViewProj(coordinates));
    },

    /**
     * @return {Promise}
     * @throws {AssertionError}
     * @protected
     */
    init: function init() {
      this.setupTransformFunctions();
      return cmp.methods.init.call(this);
    },

    /**
     * @protected
     */
    setupTransformFunctions: function setupTransformFunctions() {
      var _this2 = this;

      // define helper methods based on geometry type
      var transform = transforms[this.type].transform;
      /**
       * @method
       * @param {number[]} coordinates
       * @return {number[]}
       * @protected
       */

      this.toDataProj = function (coordinates) {
        return transform(coordinates, _this2.viewProjection, _this2.resolvedDataProjection);
      };
      /**
       * @method
       * @param {number[]} coordinates
       * @return {number[]}
       * @protected
       */


      this.toViewProj = function (coordinates) {
        return transform(coordinates, _this2.resolvedDataProjection, _this2.viewProjection);
      };
    },

    /**
     * @return {void|Promise}
     * @protected
     */
    deinit: function deinit() {
      return cmp.methods.deinit.call(this);
    },

    /**
     * @return {Promise}
     */
    refresh: function refresh() {
      return cmp.methods.refresh.call(this);
    },

    /**
     * @return {Object}
     * @protected
     */
    getServices: function getServices() {
      var vm = this;
      return mergeDescriptors(cmp.methods.getServices.call(this), {
        get geometry() {
          return vm.$geometry;
        }

      });
    },

    /**
     * @return {void}
     * @protected
     */
    mount: function mount() {
      this.$geometryContainer && this.$geometryContainer.setGeometry(this);
      this.subscribeAll();
    },

    /**
     * @return {void}
     * @protected
     */
    unmount: function unmount() {
      this.unsubscribeAll();
      this.$geometryContainer && this.$geometryContainer.setGeometry(undefined);
    },

    /**
     * @return {void}
     * @protected
     */
    subscribeAll: function subscribeAll() {
      subscribeToGeomChanges.call(this);
    }
  },
  watch: {
    id: function id(value) {
      if (!this.$geometry || value === getGeometryId(this.$geometry)) {
        return;
      }

      setGeometryId(this.$geometry, value);
    },
    coordinates: function coordinates(value) {
      if (!this.$geometry || !this.$view) return; // compares in data projection

      var isEq = isEqualGeom({
        coordinates: value,
        extent: boundingExtent(value)
      }, {
        coordinates: this.getCoordinates(),
        extent: this.extent
      });
      if (isEq) return;
      this.setCoordinates(value);
    },
    resolvedDataProjection: function resolvedDataProjection() {
      if (!this.$geometry) return;
      this.setupTransformFunctions();
      this.setCoordinates(this.coordinates);
    }
  },
  stubVNode: {
    empty: function empty() {
      return this.vmId;
    }
  },
  created: function created() {
    defineServices.call(this);
  }
};

function defineServices() {
  var _this3 = this;

  Object.defineProperties(this, {
    /**
     * @type {module:ol/geom/Geometry~Geometry|undefined}
     */
    $geometry: {
      enumerable: true,
      get: function get() {
        return _this3.$olObject;
      }
    },

    /**
     * @type {module:ol/PluggableMap~PluggableMap|undefined}
     */
    $map: {
      enumerable: true,
      get: function get() {
        return _this3.$services && _mapInstanceProperty(_this3.$services);
      }
    },

    /**
     * @type {module:ol/View~View|undefined}
     */
    $view: {
      enumerable: true,
      get: function get() {
        return _this3.$services && _this3.$services.view;
      }
    },

    /**
     * @type {Object|undefined}
     */
    $geometryContainer: {
      enumerable: true,
      get: function get() {
        return _this3.$services && _this3.$services.geometryContainer;
      }
    }
  });
}
/**
 * @return {void}
 * @private
 */


function subscribeToGeomChanges() {
  var _this4 = this;

  hasGeometry(this);
  var ft = 1000 / 60;
  var changes = observableFromOlEvent(this.$geometry, 'change', function () {
    return {
      coordinates: _this4.getCoordinates(),
      extent: _this4.extent
    };
  }).pipe(throttleTime(ft), distinctUntilChanged(isEqualGeom), map(function (_ref) {
    var coordinates = _ref.coordinates;
    return {
      prop: 'coordinates',
      value: coordinates
    };
  }));
  this.subscribeTo(changes, function (_ref2) {
    var prop = _ref2.prop,
        value = _ref2.value;
    ++_this4.rev;

    _this4.$nextTick(function () {
      _this4.$emit("update:".concat(prop), value);
    });
  });
}
/**
 * @param {{coordinates: number[], extent: number[]}} a
 * @param {{coordinates: number[], extent: number[]}} b
 * @returns {boolean}
 */


function isEqualGeom(a, b) {
  return isEqual(a.extent, b.extent) ? isEqual(a.coordinates, b.coordinates) : false;
}

export default geometry;
